// @ts-strict-ignore
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Line } from '@3d-scene/model/line';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { LabelStripTexture } from './replace-labeling-data';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ReplaceDynamicArticleUtils } from '@article/helper/replace/replace-dynamic-article-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ReplaceDynamicArticleService } from '@article/helper/replace/replace-dynamic-article.service';

@Injectable({ providedIn: 'root' })
export class ReplaceLabelingUtils {
  constructor(
    private _componentUtils: ContainerService,
    private _insertArticleHelperFactory: InsertArticleHelperFactory,
    private _articleContainerUtils: ArticleContainerUtils,
    private connectionInfoService: ConnectionInfoService,
    private replaceDynamicArticleService: ReplaceDynamicArticleService
  ) {}

  public getLabelStripsToRecreateByMoveMap(
    insertionPoint: Vector3,
    moveMap: Dictionary<string, Vector3>,
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    if (!ReplaceDynamicArticleUtils.isSuitableInsertHelper(insertHelper)) {
      return [];
    }

    const labelStripsToReCreate: ArticleContainer[] = this.findLabelStripsByPoint(insertionPoint, insertHelper);
    if (moveMap) {
      const connectedLabelStrips: ArticleContainer[] = this.getConnectedLabelStrips(moveMap.keys());
      for (const labelStrip of connectedLabelStrips) {
        if (
          labelStripsToReCreate.indexOf(labelStrip) < 0 &&
          !this.isMoveMapContainsAllBackwardUsageIdsWithSameOffset(labelStrip, moveMap, insertHelper)
        ) {
          labelStripsToReCreate.push(labelStrip);
        }
      }
    }
    return labelStripsToReCreate;
  }

  public getLabelStripsToRecreateByUsageIds(usageIds: string[]): ArticleContainer[] {
    return this.getLabelStripsToRecreateByArticles(this._componentUtils.findArticleContainersInGlobalByGuids(usageIds));
  }

  public getLabelStripsToRecreateByArticles(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const labelStrips: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      const forwardConnectedContainers: ArticleContainer[] = this.getLabelStripsToRecreateByArticle(articleContainer);
      ArrayUtils.mergeArrays(labelStrips, forwardConnectedContainers);
    }
    return labelStrips;
  }

  private getLabelStripsToRecreateByArticle(articleContainer: ArticleContainer): ArticleContainer[] {
    if (LabelingUtils.isArticleIgnoredByLabelStrip(articleContainer.article)) {
      return this.getLabelStripsToRecreateByNeighborArticles(articleContainer);
    } else {
      return this.getConnectedLabelStripsToContainer(articleContainer);
    }
  }

  private getLabelStripsToRecreateByNeighborArticles(articleContainer: ArticleContainer): ArticleContainer[] {
    const backwardUsageId = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage);
    const backwardConnection = this.connectionInfoService.findConnectionByIdentity(backwardUsageId);
    if (!ReplaceDynamicArticleUtils.isSuitableRangeOnLineConnection(backwardConnection)) {
      return [];
    }

    const insertHelper: InsertArticleHelper = this._insertArticleHelperFactory.createInstanceByAcceptingIdentity(backwardUsageId);
    if (!insertHelper) {
      return [];
    }

    const articleLine = insertHelper.getArticleLine(articleContainer);
    return !articleLine ? [] : this.findLabelStripsByLine(articleLine, insertHelper);
  }

  private getConnectedLabelStrips(articleUsageIds: string[]): ArticleContainer[] {
    const labelStrips: ArticleContainer[] = [];
    for (const usageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(usageId);
      const forwardConnectedContainers: ArticleContainer[] = this.getConnectedLabelStripsToContainer(articleContainer);
      ArrayUtils.mergeArrays(labelStrips, forwardConnectedContainers);
    }
    return labelStrips;
  }

  public getConnectedLabelStripsToContainer(container: ArticleContainer): ArticleContainer[] {
    const forwardConnectedContainers: ArticleContainer[] = this._articleContainerUtils.getConnectedArticlesRecursively(container, true);
    return forwardConnectedContainers.filter((container) => this.isLabelStripArticle(container.article));
  }

  private isMoveMapContainsAllBackwardUsageIdsWithSameOffset(
    labelStrip: ArticleContainer,
    moveArticlesMap: Dictionary<string, Vector3>,
    insertHelper: InsertArticleHelper
  ): boolean {
    const backwardUsageIds: string[] = this.getAllBackwardUsageIdsByLabelStrip(labelStrip, insertHelper.acceptingIdentity);
    return this.isMoveMapContainsAllUsageIdsWithSameOffset(moveArticlesMap, backwardUsageIds);
  }

  public getAllBackwardUsageIdsByLabelStrip(labelStrip: ArticleContainer, baseAcceptingId: MetaElementUsageIdentityVO): string[] {
    const backwardUsageIds: string[] = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageIds(labelStrip.articleUsage);
    const backwardConnectedUsageIds: string[] = this.replaceDynamicArticleService.getBackwardUsageIdsConnectedToAcceptingId(labelStrip, baseAcceptingId);
    ArrayUtils.mergeArrays(backwardUsageIds, backwardConnectedUsageIds);
    return backwardUsageIds;
  }

  public isMoveMapContainsAllUsageIdsWithSameOffset(moveArticlesMap: Dictionary<string, Vector3>, usageIdsToCheck: string[]): boolean {
    let offsetToCheck: Vector3 = null;
    for (const usageId of usageIdsToCheck) {
      const usageOffset: Vector3 = moveArticlesMap.getValue(usageId);
      if (!usageOffset) {
        return false;
      }
      if (!offsetToCheck) {
        offsetToCheck = usageOffset;
      } else if (!MathUtils.equalsNumber3D(offsetToCheck, usageOffset)) {
        return false;
      }
    }
    return true;
  }

  public isStripOnAdapter(labelStrip: ArticleContainer): boolean {
    const backwardAcceptingIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(labelStrip.articleUsage);
    return this.isStripOnAdapterByBackwardIds(backwardAcceptingIds);
  }

  public isStripOnAdapterByBackwardIds(backwardAcceptingIds: MetaElementUsageIdentityVO[]): boolean {
    return backwardAcceptingIds.some((id) => this.isAdapterIdentity(id));
  }

  private isAdapterIdentity(identity: MetaElementUsageIdentityVO): boolean {
    const acceptingContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(identity.articleUsageId);
    return ArticleUtils.isAdapterArticle(acceptingContainer.article);
  }

  public isNotConnectedReferenceIdExists(textures: LabelStripTexture[]): boolean {
    return textures.some((texture) => texture.referenceId && !texture.directAcceptingId);
  }

  public isLabelStripArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return ArticleUtils.isDynamicLabelingArticle(article) && !ArticleUtils.isCoverLabelStripArticle(article);
  }

  private findLabelStripsByPoint(point: Vector3, insertHelper: InsertArticleHelper): ArticleContainer[] {
    return this.findLabelStripsByLine(new Line(point, point), insertHelper);
  }

  private findLabelStripsByLine(line: Line, insertHelper: InsertArticleHelper): ArticleContainer[] {
    if (!line || !ReplaceDynamicArticleUtils.isSuitableInsertHelper(insertHelper)) {
      return [];
    }

    const leftContainer: ArticleContainer = insertHelper.getLeftArticleContainerByPoint(line.start, LabelingUtils.TYPE_IDS_IGNORED_BY_STRIP);
    const rightContainer: ArticleContainer = insertHelper.getRightArticleContainerByPoint(line.end, LabelingUtils.TYPE_IDS_IGNORED_BY_STRIP);
    if (!leftContainer || !rightContainer) {
      return [];
    }
    return this.getCommonLabelStripUsageIdsByLeftRightArticles(leftContainer, rightContainer);
  }

  public getCommonLabelStripUsageIdsByLeftRightArticles(leftContainer: ArticleContainer, rightContainer: ArticleContainer): ArticleContainer[] {
    const leftStripContainers: ArticleContainer[] = this.getConnectedLabelStripsToContainer(leftContainer);
    const rightStripContainers: ArticleContainer[] = this.getConnectedLabelStripsToContainer(rightContainer);
    const commonContainers: ArticleContainer[] = [];
    for (const rightContainer of rightStripContainers) {
      if (leftStripContainers.indexOf(rightContainer) >= 0) {
        commonContainers.push(rightContainer);
      }
    }
    return commonContainers;
  }

}
